<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`" ref="rootElementRef" class="w-100"></div>
</template>

<script>
import { getDataUrl, getNeutralColors,getHexColorByPosition,measureText,formatCash,getCurrencies } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';
import * as d3v4 from "d3v4";

  export default {
    name: 'AttackZonesChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      legendTitle:{
        type:String,
        default: ""
      },
      legendSubtitle:{
        type:String,
        default: ""
      },
      data:{
        type: Array,
        default: () => {
          return []
        }
      },
      type:{
        type: String,
        default: "start"
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {}
      }
    },
    computed: {
      SVG_WIDTH(){
        this.width;
        if(this.size){
          return this.size
        }else if(this.$refs.rootElementRef){
          return this.$refs.rootElementRef.getBoundingClientRect().width;
        }else if(document.getElementById(this.rootElementId) && document.getElementById(this.rootElementId).getBoundingClientRect()){
          return document.getElementById(this.rootElementId).getBoundingClientRect().width;
        }else{
          return 420;
        }
      },
      PITCH_WIDTH(){
        return this.SVG_WIDTH - this.CHART_MARGIN.left - this.CHART_MARGIN.right
      },
      PITCH_HEIGHT() {
        return this.PITCH_WIDTH * 0.67125;
      },
      rootElementId(){
        return `attackZonesChart${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      CHART_MARGIN(){
        return this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};
      },
    },
    async mounted() {
      await this.$nextTick();
      this.width = null;
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.render();
    },
    methods: {
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      },
      async render(){
        d3v4.select(`#${this.rootElementId} > svg`).remove();
        d3.selectAll('.tooltip').remove();

        const tooltip = d3v4
          .select("body")
          .append("div")
          .attr("class", "tooltip no-arrow");

        var svg = d3v4.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", this.SVG_WIDTH)
          .attr("height", this.PITCH_HEIGHT)
          .style("background-color", "rgba(255,255,255,0.1)")
          .attr("class", "")
          .attr("id", `${this.rootElementId}SVG`)
          .style("font-family","NeueMontreal")

        this.drawImageBase64(svg, this.CHART_MARGIN.left, this.CHART_MARGIN.top, this.PITCH_WIDTH, this.PITCH_HEIGHT,
          require(`@/assets/img/pitch_${this.currentUserFieldColor}.png`),"pitch",true);

        this.renderChart(svg)

        this.renderLegend(svg);

        this.renderLogo(svg);

        let font = await loadFonts();
        copyAndAppendElement(svg.node(),font.node(),0,0);
      },
      drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        if(!this.images[idName]){
          let base_image = new Image();
          base_image.crossOrigin = "anonymous";
          base_image.src = img;
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(
            svg,
            x,
            y,
            width,
            height,
            dataUrl,
            idName,
            bottom
            );
          }
        }else{
          if(bottom){
            svg.node().insertBefore(this.images[idName],svg.node().firstChild);
          }else{
            svg.node().appendChild(this.images[idName]);
          }
        }
      },
      drawImage(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",":first-child") : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", "NeueMontreal")
          .style("fill", fill)
          .text(text);
      },
      renderLegend(svg){
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        this.writeText(svg, 0.0875 * this.PITCH_WIDTH , 0.0375 * this.PITCH_HEIGHT, "start", (0.016 * this.SVG_WIDTH) + "px",
        `${ this.legendTitle }`,textColor);
        this.writeText(svg, 0.0875 * this.PITCH_WIDTH , 0.0675 * this.PITCH_HEIGHT, "start", (0.014 * this.SVG_WIDTH) + "px",
        `${ this.legendSubtitle }`,textColor);

        this.writeText(svg, 0.92 * this.PITCH_WIDTH , 0.0375 * this.PITCH_HEIGHT, "end", (0.016 * this.SVG_WIDTH) + "px",
        `${ this.crossings.length } ${this.$t("team.crossings")}`,textColor);
        this.writeText(svg, 0.92 * this.PITCH_WIDTH , 0.0675 * this.PITCH_HEIGHT, "end", (0.014 * this.SVG_WIDTH) + "px",
        `${ _.filter(this.crossings,(c) => c.outcome).length } ${this.$t("eventChart.successful")}`,textColor);
      },
      renderChart(svg){
        let shadowScale = d3.scale.linear().domain([0, this.data.length]).range([0, 1.2]);
        let possessionsGrouped = _.chain(this.data).groupBy(this.type === 'start' ? 'startSide' : 'endSide').forEach((group,key)=> {
          if(!_.isEmpty(group)){
            let boxX = this.SVG_WIDTH * 0.09;
            let boxWidth = this.SVG_WIDTH - (this.SVG_WIDTH * 0.087 * 2);
            let boxY = this.PITCH_HEIGHT * 0.0975;
            let boxHeight = this.PITCH_HEIGHT * (0.333 - 0.063);
            if(key === 'left'){
              boxY = boxY ;
            }else if(key === 'center'){
              boxY = boxY + boxHeight;
            }else if(key === 'right'){
              boxY = boxY + (boxHeight * 2)
            }
            //Draw rectangle
            this.drawRectangle(svg, boxX, boxY, boxWidth, boxHeight, "red", shadowScale(group.length), `box-${key}`);
            //Draw arrow
            let arrowWidth = this.SVG_WIDTH * 0.66;
            let arrowHeight = this.PITCH_HEIGHT * 0.14
            let arrowX = this.SVG_WIDTH * 0.275;
            let arrowY = boxY + (boxHeight/2) - (arrowHeight/2);
            this.drawImageBase64(svg, arrowX,arrowY,arrowWidth,arrowHeight,require(`@/assets/img/attack_arrow.png`),`attackArrow-${key}`)
            this.writeText(svg,this.SVG_WIDTH * 0.575,arrowY + (this.PITCH_HEIGHT * 0.06),'middle',this.SVG_WIDTH * 0.01,this.$t(`general.${key}`),"white",'');

            //Draw text
            let textX = this.SVG_WIDTH * 0.35;
            let text1Y = arrowY + (this.PITCH_HEIGHT * 0.03);
            let text1 = `${((_.size(group) / _.size(this.data)) * 100).toFixed(1)}%`;
            this.writeText(svg,textX,text1Y,'start',this.SVG_WIDTH * 0.025,text1,"white",'');
            let text2Y = arrowY + (this.PITCH_HEIGHT * 0.075);
            let text2 = `${_.size(group)} ${this.$t("game.header.possessions")}`;
            this.writeText(svg,textX,text2Y,'start',this.SVG_WIDTH * 0.015,text2,"white",'');
          }
        }).value()
      },
      renderLegend(svg){
        let fontColor = "white"
        //Title
        svg
          .append("text")
          .attr("x", this.SVG_WIDTH * 0.09)
          .attr("y", this.PITCH_HEIGHT * 0.05)
          .attr("text-anchor","start")
          .style("font-size",this.SVG_WIDTH * 0.025)
          .style("font-weight",600)
          .style("fill",fontColor)
          .text(this.legendTitle);

        //Subtitle
        svg
          .append("text")
          .attr("x", this.SVG_WIDTH * 0.09)
          .attr("y",this.PITCH_HEIGHT * 0.075)
          .attr("text-anchor","start")
          .style("font-size",this.SVG_WIDTH * 0.012)
          .style("fill",fontColor)
          .text(this.legendSubtitle);

        //Total possessions
        svg
          .append("text")
          .attr("x", this.SVG_WIDTH * 0.91)
          .attr("y",this.PITCH_HEIGHT * 0.075)
          .attr("text-anchor","end")
          .style("font-size",this.SVG_WIDTH * 0.012)
          .style("fill",fontColor)
          .text(`${this.data.length} ${this.$t("game.header.possessions")}`);
      },
      renderLogo(svg){
        // Draw Driblab Logo
        const logoWidth = this.SVG_WIDTH * 0.22;
        const logoHeight = this.PITCH_HEIGHT * 0.1;
        let base_image = new Image();
        base_image.src = require("@/assets/img/driblab_white.png");
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          let imgNode = svg.append("svg:image")
          .attr("x", this.SVG_WIDTH * 0.745)
          .attr("y", this.PITCH_HEIGHT * 0.825)
          .attr("width", logoWidth * 0.77)
          .attr("height", logoHeight * 0.77)
          .attr("xlink:href", dataUrl);
        }
      }
    },
    destroyed() {
      delete this.observer;
      window.removeEventListener("resize",this.render);
    },
  }

</script>

<style lang="scss">


</style>



// WEBPACK FOOTER //
// src/views/base/AttackZonesChart.vue